/**
 * Loads tour prefs, builds steps, shows overlay on first login / replay.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getProfilePreferences,
  updateProfilePreferences,
  PRODUCT_TOUR_VERSION,
} from '../../api.js';
import type { SessionUser } from '../../types.js';
import type { ManifestNavItem } from '../../pages/Shell.js';
import { buildProductTourSteps } from './build-steps.js';
import { ProductTourOverlay } from './ProductTourOverlay.js';
import { useProductTour } from './ProductTourContext.js';

interface ProductTourHostProps {
  session: SessionUser;
  productName: string;
  nav: ManifestNavItem[];
  billingEnabled: boolean;
  /** True when Shell is showing chat (path is /). */
  onChat: boolean;
  /** Billing modal open — do not start tour. */
  billingOpen: boolean;
  /** Chat boot finished (sidebar/composer mounted). */
  chatBootReady: boolean;
}

export function ProductTourHost({
  session,
  productName,
  nav,
  billingEnabled,
  onChat,
  billingOpen,
  chatBootReady,
}: ProductTourHostProps) {
  const { chatControls, forceReplayToken } = useProductTour();
  const [prefsReady, setPrefsReady] = useState(false);
  const [shouldAutoShow, setShouldAutoShow] = useState(false);
  const [active, setActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [replayPending, setReplayPending] = useState(false);

  const canRun = session.type === 'user';

  // Load prefs once for domain users
  useEffect(() => {
    if (!canRun) {
      setPrefsReady(true);
      setShouldAutoShow(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const prefs = await getProfilePreferences();
        if (cancelled) return;
        const show =
          !prefs.product_tour_completed ||
          prefs.product_tour_version !== PRODUCT_TOUR_VERSION;
        setShouldAutoShow(show);
        setLoadError(null);
      } catch (e) {
        if (cancelled) return;
        // Fail fast: surface error, do not invent completed or auto-start
        setLoadError(e instanceof Error ? e.message : String(e));
        setShouldAutoShow(false);
      } finally {
        if (!cancelled) setPrefsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canRun, session.slug]);

  // Force replay from Commands
  useEffect(() => {
    if (forceReplayToken > 0) {
      setReplayPending(true);
    }
  }, [forceReplayToken]);

  // Start when ready
  useEffect(() => {
    if (!canRun || !prefsReady || !onChat || billingOpen || !chatBootReady) return;
    if (active) return;
    if (shouldAutoShow || replayPending) {
      setActive(true);
      setReplayPending(false);
      setError(null);
    }
  }, [
    canRun,
    prefsReady,
    onChat,
    billingOpen,
    chatBootReady,
    shouldAutoShow,
    replayPending,
    active,
  ]);

  const steps = useMemo(
    () =>
      buildProductTourSteps({
        productName,
        nav: nav.map((n) => ({ id: n.id, label: n.label })),
        billingEnabled,
        canChangePassword: session.type === 'user',
        showSidebarAdmin: session.type === 'admin',
      }),
    [productName, nav, billingEnabled, session.type],
  );

  const persistComplete = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await updateProfilePreferences({
        product_tour_completed: true,
        product_tour_version: PRODUCT_TOUR_VERSION,
      });
      setShouldAutoShow(false);
      setActive(false);
      chatControls?.closeMobileSidebar();
      chatControls?.focusComposer();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [chatControls]);

  const handleSkip = useCallback(() => {
    void persistComplete();
  }, [persistComplete]);

  const handleComplete = useCallback(() => {
    void persistComplete();
  }, [persistComplete]);

  if (loadError && canRun) {
    return (
      <div className="pointer-events-none fixed bottom-3 left-1/2 z-[55] max-w-sm -translate-x-1/2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-[11px] text-amber-900 shadow-soft">
        Product tour prefs failed to load: {loadError}
      </div>
    );
  }

  if (!active || steps.length === 0) return null;

  return (
    <ProductTourOverlay
      steps={steps}
      chatControls={chatControls}
      onSkip={handleSkip}
      onComplete={handleComplete}
      saving={saving}
      error={error}
    />
  );
}
